import React, { startTransition, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Divider, Stack, Typography, useTheme } from '@mui/material'

import { SideNav } from '@repo/ui'
import { Search } from 'components'
import { KeyChip } from 'components/display'
import { SegmentedTabs } from 'components/navigation/SegmentedTabs/SegmentedTabs'

import { useIsInternalUser } from 'hooks/useIsInternalUser'
import { useSearchStore } from 'search'
import { ROUTES } from 'routes'

import { DOCS_HEADER_HEIGHT, DOCS_STICKY_TOP } from './constants'

import type { DocEntry } from 'docs'
import type { DocsSource } from 'docs/source'

const keyOf = (doc: DocEntry) => `${doc.category}/${doc.slug}`
const ALL_DOCS = '__all__'
/**
 * The glossary row's sentinel value.
 *
 * Not a `category/slug` like every other row, because the glossary is not a guide — it is
 * generated from `docs/glossary.json`, so it has no `DocEntry` to key on.
 *
 * It needs a row at all because the page was ORPHANED: `DOCS_GLOSSARY` appeared in exactly two
 * places in the app, the route constant and the `<Route>`, so the only way to reach a fully built
 * searchable glossary was a tooltip footer or typing the URL. `reference` was deliberately
 * reserved out of the guide category set so this path could live under /docs; the nav entry was
 * the missing half of that.
 */
export const GLOSSARY_NAV_VALUE = '__glossary__'

/** ⌘ on Apple platforms, Ctrl everywhere else. Read once — the platform cannot change. */
const IS_APPLE =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform || '')
const SHORTCUT_LABEL = IS_APPLE ? '⌘K' : 'Ctrl K'

/**
 * Search entry point for the docs, on the shared `Search` field.
 *
 * It is the shared component, not a bespoke button, so it carries the app's field
 * treatment for free (§31 — one source for border, focus ring and adornment inset).
 *
 * It is also an HONEST field, which is the part worth keeping: the ⌘K palette owns the
 * query, but TYPING HERE WORKS — the first keystroke opens the palette seeded with what
 * you typed (`openSearch(value)`), and this field resets. So it is not an `<input>` that
 * silently swallows input, which is the trap the sidebar trigger fell into. Clicking
 * opens the empty palette.
 *
 * `minWidth: 0` overrides the shared field's own 300px floor. That floor is meant for a
 * search above a data table; here it was the thing forcing the nav column wide.
 */
const DocsSearchTrigger = () => {
  const theme = useTheme()
  const openSearch = useSearchStore(s => s.openSearch)
  // Bumped on every handoff to remount the field empty. The shared `Search` owns its text
  // internally and only re-reads `value` when that prop CHANGES — passing a constant ''
  // does not clear it — so the query stayed visible here after the palette took it.
  const [resetKey, setResetKey] = useState(0)

  return (
    <Box
      onClick={() => openSearch()}
      sx={{ position: 'relative', width: '100%', cursor: 'pointer' }}
    >
      <Search
        key={resetKey}
        // "Search docs", not "Search the docs": the column is 192px at 1280, and with the
        // icon and the shortcut hint both taking room the longer string truncated.
        placeholder="Search docs"
        // Held empty on purpose: the palette is where a query lives, so this field is
        // never the thing holding one.
        value=""
        debounce={false}
        fullWidth
        onChange={value => {
          if (!value) return
          openSearch(value)
          setResetKey(k => k + 1)
        }}
        // The shortcut hint is positioned over the field's right edge, so the text needs
        // room reserved or the placeholder runs under it — at the narrow end of this
        // column it rendered as "Search the do⌘K".
        sx={{ minWidth: 0, cursor: 'pointer', '& input': { pr: 8 } }}
      />
      {/* The shared KeyChip, the same component the sidebar search trigger and the Help
          popover use for a shortcut hint — this was bare text, which is the one place in the
          app a chord rendered without its chip.
          aria-hidden because `useSearchHotkey` owns the real binding and a screen reader
          would read the glyph as punctuation. Positioned rather than passed as an adornment:
          the shared field spends its end adornment on the clear button. */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          right: 12,
          top: '50%',
          transform: 'translateY(-50%)',
          pointerEvents: 'none',
          display: 'flex',
        }}
      >
        <KeyChip muted>{SHORTCUT_LABEL}</KeyChip>
      </Box>
    </Box>
  )
}

/**
 * Internal / External — which docs SITE the reader is on. Staff only.
 *
 * ## It is navigation between two artifacts, not a filter over one
 *
 * This is the part that must not drift. There are two separately-published bundles —
 * `search-index.json` (customer) and `internal-search-index.json` (staff) — and this control
 * does exactly one thing: `navigate()` to the other site's index route. It never reads an
 * audience field, never merges the two catalogs, and never sees a document it is not already
 * rendering. Each route then mounts its own source, bound to its own manifest at module level.
 *
 * That is a deliberate structural choice, not an implementation detail. working-conventions §43
 * exists because `roles-and-permissions.md` — an engineering reference naming `@Permissions`
 * decorators — shipped to brand customers. A per-document `audience` flag evaluated in the
 * browser would recreate exactly that failure mode: one corpus, one predicate, and one bug away
 * from an internal runbook rendering on a customer's screen. Two bundles cannot leak into one
 * another, because the customer bundle does not CONTAIN the internal guides in any form.
 *
 * Concretely, for anyone extending this: importing `INTERNAL_DOCS` here would be the wrong move
 * even though it looks tidier than a route string. `docs/sources.internal.ts` documents why —
 * the internal MANIFEST carries every internal guide's title, excerpt and headings, and this
 * file is reachable from the customer docs pages, so importing it would ship internal guide text
 * to every brand user. Routes are inert strings; sources are not.
 *
 * ## Why staff-only, and why hidden rather than disabled
 *
 * §27: hide whole surfaces a user cannot reach, disable in-context actions they merely lack
 * permission for. A brand user has no path to `/internal/docs` at all — `InternalRoute` bounces
 * them — so a disabled "Internal" tab would advertise the existence of staff tooling and give
 * them nothing to do about it. `useIsInternalUser` carries the `!inSpace` clause, so a staffer
 * operating inside a brand's space sees no toggle either, which is right: in that state they are
 * acting as that brand's user.
 *
 * ## Clicking the ACTIVE side does nothing
 *
 * This replaced an "All docs" row, and an earlier version of it kept that row's behaviour —
 * clicking the active side went to that site's index. That was wrong, and the reasoning behind
 * it did not survive: "All docs" was a LINK, whose entire purpose was to go somewhere, whereas a
 * segmented control's active side is a STATE INDICATOR. Clicking the state you are already in and
 * being thrown to an index — losing the guide you were reading — is the one interaction a toggle
 * must not have; it reads as a bug, not as navigation.
 *
 * So the active side is a no-op and only the inactive side navigates.
 *
 * ## Where switching sites lands you
 *
 * On the other site's INDEX, always — never an "equivalent" guide.
 *
 * Not a fallback for want of a mapping, but the correct rule. §43's entire point is that these
 * are two SITES, not one corpus rendered twice: the operator's "what each role can do" and the
 * engineer's "where role gating is wired" are different documents that happen to share a subject.
 * So there is no such thing as the same guide on the other side, and silently landing a reader on
 * a document that merely shares a slug would assert an equivalence the docs contract denies.
 * Measured, the two corpora share ZERO slugs today, so a mapping would also be dead code.
 */
/**
 * The site the toggle was showing when it was clicked, handed to the instance that replaces it.
 *
 * Module scope rather than router state, and that is a decision worth defending. Router state
 * is stored ON THE HISTORY ENTRY, so it survives a reload and replays on back/forward: land on
 * /internal/docs, reload, and the pill would slide across on a page that was merely loaded. The
 * animation should mean "you just switched", which is true of exactly one thing — the click.
 * A module variable is scoped to precisely that click-to-remount handoff, dies with the tab,
 * and keeps `location.state` free for the referrer contract (§42) that already lives there.
 *
 * Set on click, read on the next mount, cleared by that instance's mount effect. Both the wide
 * and the narrow toggle read it (only one is visible at a time, but both are rendered), which
 * is why the read does not clear — the clear is deferred to an effect, after every instance in
 * the commit has had its turn.
 */
let pendingSiteSwitch: DocsSource['id'] | null = null

/**
 * Fetch the internal docs chunk ahead of the click.
 *
 * The specifier matches `App.tsx`'s `React.lazy` exactly so both resolve to the same chunk;
 * a different-but-equivalent path would risk a second one.
 *
 * §43 is intact: this is a DYNAMIC import, so the internal manifest stays in its own chunk and
 * this module (which the customer docs pages import eagerly) gains only a chunk URL — the same
 * arrangement `App.tsx` already has. And the only caller is inside the toggle, which renders
 * for `useIsInternalUser()` only, so a brand user never triggers the fetch either.
 */
const preloadInternalDocs = () => import('pages/internal/InternalDocsPage')

const DocsSiteToggle = ({ source }: { source: DocsSource }) => {
  const navigate = useNavigate()
  // Captured on the first render and never updated — `useRef`'s argument is evaluated each
  // render but only the first is kept, which is exactly the one-shot read this needs (and it
  // is safe under StrictMode's double render, where clearing during render would not be).
  const enterFrom = useRef(pendingSiteSwitch ?? undefined).current

  useEffect(() => {
    // The handoff has been consumed by every toggle in this commit; a later mount (a reload, a
    // back navigation) must not inherit it.
    pendingSiteSwitch = null
    // Warm the other site's chunk now rather than on click. Without it the first switch spends
    // the fetch inside the route's Suspense boundary, which is measurable: instrumented on the
    // live page, /docs -> /internal/docs showed an 85ms window with the App-level <Loader/> up
    // and no toggle in the DOM at all. Staff-only by construction (see above).
    if (source.id === 'customer') void preloadInternalDocs()
  }, [source.id])

  return (
    <SegmentedTabs
      enterFrom={enterFrom}
      // fullWidth so the track can never exceed the column, and compact so the labels fit
      // inside it — measured at 163px, where the default 16px tab padding needs 184.
      fullWidth
      compact
      tabs={[
        // The values are the DocsSource discriminators (`source.id`), not prettier synonyms,
        // so the active tab is read straight off the source being rendered rather than from a
        // second mapping that could disagree with it.
        { label: 'Internal', value: 'internal' },
        { label: 'External', value: 'customer' },
      ]}
      value={source.id}
      onChange={id => {
        // The active side is a state indicator, not a link — see the note above.
        if (id === source.id) return
        pendingSiteSwitch = source.id
        // In a transition, so React keeps the CURRENT page on screen while the other site's
        // route resolves instead of replacing it with the App-level <Loader/>. A loader between
        // the two states breaks the continuity this control is meant to express — the pill
        // would slide only after the page had blanked and come back. With the preload above the
        // wait is normally sub-frame anyway; this covers the cold first click.
        startTransition(() => {
          navigate(id === 'internal' ? ROUTES.INTERNAL_DOCS : ROUTES.DOCS)
        })
      }}
    />
  )
}

interface Props {
  /** The guide being read, when the page IS a guide. Absent on the glossary. */
  current?: DocEntry
  /** Selected row when there is no `current` — the glossary's sentinel. */
  activeValue?: string
  source: DocsSource
}

/**
 * The left column: every published guide, grouped by category.
 *
 * It is also the page's back-navigation, which is why an "All docs" row sits at the
 * top. The shared `Header` breadcrumb is the usual way back (§ detail-page back-nav),
 * but it derives its crumbs from the URL, and the middle segment of
 * /docs/<category>/<slug> has no page behind it — a `Docs / Settings / …` breadcrumb
 * would put a dead link in the trail. A permanently visible index of the whole docs set
 * is better navigation for a documentation surface than a three-crumb trail anyway.
 */
export const DocSectionNav = ({ current, activeValue, source }: Props) => {
  const navigate = useNavigate()
  const isInternal = useIsInternalUser()

  const go = (value: string) => {
    if (value === ALL_DOCS) {
      navigate(source.indexPath)
      return
    }
    if (value === GLOSSARY_NAV_VALUE) {
      navigate(ROUTES.DOCS_GLOSSARY)
      return
    }
    const [category, slug] = value.split('/')
    // The overview guide is reachable at its own /docs/<category>/<slug> path, but the
    // canonical URL for it is the index. Sending the row there keeps one address for one
    // page instead of two that render identically.
    if (source.overviewSlug && slug === source.overviewSlug) {
      navigate(source.indexPath)
      return
    }
    navigate(source.docPath(category, slug))
  }

  const rows = (docs: DocEntry[]) =>
    docs.map(d => ({ label: d.title, value: keyOf(d) }))

  // A guide selects its own row; the glossary selects via `activeValue`. Empty string selects
  // nothing, which is what a source with no current page wants.
  const selected = current ? keyOf(current) : activeValue ?? ''

  // The overview stays IN its category rather than getting a row of its own above the
  // sections: it is the first thing to read in Getting started, which is exactly where a
  // reader looks for it, and a bespoke top row would need explaining.
  const overview = source.overviewSlug
    ? source.docs.find(d => d.slug === source.overviewSlug)
    : undefined
  const groupDocs = (categoryId: string) => source.docsInCategory(categoryId)

  return (
    <>
      {/* Wide: the full grouped index, pinned so it stays with the reader through a
          long page. */}
      {/*
        VIEWPORT-HEIGHT COLUMN, with the index scrolling INSIDE it.

        `position: sticky` alone was not enough, and the way it failed is the non-obvious
        part. A sticky element TALLER than the viewport does not pin at `top` — it scrolls
        with the page until its own BOTTOM reaches the viewport bottom, and only then stops.
        The index is ~1200px, so at a 1280x800 laptop the whole column, search field
        included, rode up and out: measured on a doc page, the field's top went from 61.5px
        to -411px. It reads as "the search scrolled away", because it did.

        Capping the column at the viewport turns the overflow into an INTERNAL scroll, so
        the field stays put and the index still reaches its last row. `minHeight: 0` on the
        scroller is required — a flex child's default `min-height: auto` refuses to shrink
        below its content, which silently reinstates the tall-column behaviour.

        Not `overflow: hidden` on the column: the SideNav rail's active-row indicator sits
        just outside the row box and would be clipped.
      */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          position: 'sticky',
          top: `${DOCS_STICKY_TOP}px`,
          height: `calc(100vh - ${DOCS_STICKY_TOP * 2}px)`,
        }}
      >
        {/* The shared header band — see DOCS_HEADER_HEIGHT. The search field centres in it,
            which is what puts it on the document title's line without either column
            knowing the other's font size. */}
        {/* No horizontal inset: the field spans the column's full width, so it reads as the
            head OF the nav rather than as its first row. The rows below keep their own 12px
            inset, which is why the field extends slightly past them on both sides. */}
        {/* `flexShrink: 0` — this is the one part of the column that must never compress
            or scroll. It is the reason the column is capped at all. */}
        <Box
          sx={{
            height: DOCS_HEADER_HEIGHT,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <DocsSearchTrigger />
        </Box>
        {/* Everything below the field scrolls. `minHeight: 0` is load-bearing (see above);
            the negative/positive inset pair gives the rail's active indicator room to paint
            without being clipped by the scroll box. */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            mx: -2,
            px: 2,
          }}
        >
        {/* Staff get the site toggle in this slot. For everyone else the old rule stands: only
            a source WITHOUT an overview needs a row back to a card index, because where there
            IS one the overview is simply the first row of its own section (Vergence does the
            same) and the nav has no special top entry to explain. */}
        {isInternal ? (
          <Box sx={{ mt: 4 }}>
            <DocsSiteToggle source={source} />
          </Box>
        ) : (
          !overview && (
            <Box sx={{ mt: 4 }}>
              <SideNav items={[{ label: 'All docs', value: ALL_DOCS }]} value="" onChange={go} />
            </Box>
          )
        )}
        {source.categories.map((category, index) => {
          const docs = groupDocs(category.id)
          if (!docs.length) return null
          return (
            // 16px between groups — the rows gained 4px each, so the column pays for that
            // partly out of the gaps rather than entirely out of the viewport.
            //
            // The FIRST group gets 32px. That gap is not a group-to-group gap: it separates
            // the search field from the contents list, and those are different KINDS of
            // thing — chrome versus the document index. At the same 16px the search read as
            // the list's first row rather than as the column's head.
            <Box key={category.id} sx={{ mt: index === 0 ? 8 : 4 }}>
              <Typography
                variant="body4"
                color="text.primary"
                sx={{
                  display: 'block',
                  px: 3,
                  mb: 1,
                  textTransform: 'uppercase',
                  letterSpacing: 0.8,
                  // 700 and primary, not 600 and secondary. A section head competing with
                  // the rows beneath it at the same colour and nearly the same weight is
                  // the flatness that made this column read as one long list.
                  fontWeight: 700,
                }}
              >
                {category.label}
              </Typography>
              {/* Restored, and spanning the column rather than inset: it is what makes the
                  label read as a HEADING OVER the rows rather than as one more small row
                  above them, and it is what the reference layout does. Removing it in the
                  previous pass bought ~45px of height and cost the group its head. */}
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ pl: 2 }}>
                <SideNav
                  variant="rail"
                  items={rows(docs)}
                  value={selected}
                  onChange={go}
                />
              </Box>
            </Box>
          )
        })}

        {/* REFERENCE — the glossary, kept in its own group BELOW the guides rather than
            slipped into one of them. It is a different kind of thing: the guides are prose
            you read in an order, the glossary is a lookup you jump into from a tooltip. Its
            own heading says so, and matches the URL's reserved `reference` segment. */}
        <Box sx={{ mt: 4 }}>
          <Typography
            variant="body4"
            color="text.primary"
            sx={{
              display: 'block',
              px: 3,
              mb: 1,
              textTransform: 'uppercase',
              letterSpacing: 0.8,
              fontWeight: 700,
            }}
          >
            Reference
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ pl: 2 }}>
            <SideNav
              variant="rail"
              items={[{ label: 'Glossary', value: GLOSSARY_NAV_VALUE }]}
              value={selected}
              onChange={go}
            />
          </Box>
        </Box>
        </Box>
      </Box>

      {/* Narrow: the side columns collapse, so this folds to a scrollable pill row —
          the same pattern Settings uses for its sub-nav. It carries only the CURRENT
          category plus a way back to the index, rather than all published guides: a
          17-pill strip is not navigation, and the full grouped set already has a home
          on the index page. */}
      <Stack
        spacing={2}
        sx={{ display: { xs: 'flex', md: 'none' }, mb: 6 }}
      >
        {/* The toggle comes along to the narrow layout rather than being a wide-only luxury:
            it is the only way to reach the other site from here, and a staffer on a laptop at
            a narrow window would otherwise be stranded on whichever site they landed on. */}
        {isInternal && <DocsSiteToggle source={source} />}
        <Typography variant="body4" color="text.secondary">
          {current ? source.categoryLabel(current.category) : 'Reference'}
        </Typography>
        <SideNav
          direction="horizontal"
          items={[
            // Same rule as the wide layout: the "All docs" pill only earns its place on a
            // source whose index is a card grid, and only where the toggle above has not
            // already taken over that job. With an overview, that page is already in the
            // strip as its own row.
            ...(overview || isInternal ? [] : [{ label: 'All docs', value: ALL_DOCS }]),
            ...(current ? rows(groupDocs(current.category)) : []),
            { label: 'Glossary', value: GLOSSARY_NAV_VALUE },
          ]}
          value={selected}
          onChange={go}
        />
      </Stack>
    </>
  )
}
